import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Discover.module.scss";
import { api } from "../../services/api";
import { useForm } from "react-hook-form";
import UserCard from "../../components/UserCard";
import { useUser } from "../../context/UserContext";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { SkeletonContainer } from "../../components/Skeleton";
import { DiscoverSkeleton } from "./components/DiscoverSkeleton";
import type { GameListItem } from "../../services/api/GamesApi";
import noImage from "../../assets/no-image.jpg";

type SearchFormValues = {
  search: string;
};

type DiscoverUser = {
  userId: string;
  username: string;
  avatarUrl?: string;
  profileImageUrl?: string;
  status?: string | null;
  isFollowing?: boolean;
};

type Tab = "users" | "games";

const Discover: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("users");
  const { user: currentUser } = useUser();
  const navigate = useNavigate();

  // ─── Users state ───
  const [users, setUsers] = useState<DiscoverUser[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const { fetchStatuses } = useOnlineStatus();

  // ─── Games state ───
  const [games, setGames] = useState<GameListItem[]>([]);
  const [gamesLoading, setGamesLoading] = useState<boolean>(false);
  const [gamesQuery, setGamesQuery] = useState<string>("");
  const [gamesCursor, setGamesCursor] = useState<string | null>(null);
  const [gamesHasMore, setGamesHasMore] = useState<boolean>(true);
  const gamesSentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SearchFormValues>({
    defaultValues: { search: "" },
  });

  // ─── Users logic ───
  const fetchUsers = async (search: string = "") => {
    setUsersLoading(true);
    try {
      const resp = await api.app.getUsers(search);
      const usersList = resp.users ?? [];

      const usersWithFollowStatus = await Promise.all(
        usersList.map(async (user: DiscoverUser) => {
          try {
            const { isFollowing } = await api.social.isFollowing(user.userId);
            return { ...user, isFollowing };
          } catch (error) {
            return { ...user, isFollowing: false };
          }
        })
      );

      setUsers(usersWithFollowStatus);

      const userIds = usersWithFollowStatus.map((u: DiscoverUser) => u.userId);
      if (userIds.length > 0) {
        fetchStatuses(userIds);
      }
    } catch (e) {
      console.error(e);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // ─── Games logic ───
  const fetchGames = useCallback(
    async (query: string, cursor?: string | null) => {
      setGamesLoading(true);
      try {
        const resp = await api.games.listGames({
          q: query || undefined,
          limit: 24,
          cursor: cursor || undefined,
        });

        if (cursor) {
          setGames((prev) => [...prev, ...resp.games]);
        } else {
          setGames(resp.games);
        }
        setGamesCursor(resp.nextCursor);
        setGamesHasMore(resp.hasMore);
      } catch (e) {
        console.error(e);
        if (!cursor) setGames([]);
        setGamesHasMore(false);
      } finally {
        setGamesLoading(false);
      }
    },
    []
  );

  // Initial fetch
  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers("");
    } else {
      setGames([]);
      setGamesCursor(null);
      setGamesHasMore(true);
      fetchGames(gamesQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Infinite scroll for games
  useEffect(() => {
    if (activeTab !== "games") return;

    const sentinel = gamesSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && gamesHasMore && !gamesLoading && gamesCursor) {
          fetchGames(gamesQuery, gamesCursor);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeTab, gamesHasMore, gamesLoading, gamesCursor, gamesQuery, fetchGames]);

  // ─── Submit handlers ───
  const onSubmitUsers = async (data: SearchFormValues) => {
    await fetchUsers(data.search);
  };

  const onSubmitGames = async (data: SearchFormValues) => {
    const q = data.search.trim();
    setGamesQuery(q);
    setGames([]);
    setGamesCursor(null);
    setGamesHasMore(true);
    await fetchGames(q);
  };

  const handleFollowChange = (userId: string, isFollowing: boolean) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.userId === userId ? { ...user, isFollowing } : user
      )
    );
  };

  return (
    <div className={styles.discoverPage}>
      {/* Search Bar */}
      <form
        className={styles.searchBar}
        onSubmit={handleSubmit(activeTab === "users" ? onSubmitUsers : onSubmitGames)}
      >
        <input
          type="text"
          placeholder={activeTab === "users" ? "Search users…" : "Search games…"}
          aria-label={activeTab === "users" ? "Search users" : "Search games"}
          {...register("search")}
        />
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <span className={styles.btnSpinner} /> : "Search"}
        </button>
      </form>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "users" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          className={`${styles.tab} ${activeTab === "games" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("games")}
        >
          Games
        </button>
      </div>

      {/* Users Tab Content */}
      {activeTab === "users" && (
        <SkeletonContainer
          loading={usersLoading}
          skeleton={<DiscoverSkeleton />}
          label="users"
        >
          <div className={styles.usersList}>
            {users.length === 0 ? (
              <div className={styles.noResults}>No users found.</div>
            ) : (
              users.map((u) => (
                <UserCard
                  key={u.userId}
                  user={u}
                  status={u.status ?? undefined}
                  currentUserId={currentUser?.userId}
                  initialIsFollowing={u.isFollowing ?? false}
                  initialHasPendingRequest={u.hasPendingRequest ?? false}
                  isTargetPrivate={u.isPrivate ?? false}
                  onFollowChange={(isFollowing) => handleFollowChange(u.userId, isFollowing)}
                />
              ))
            )}
          </div>
        </SkeletonContainer>
      )}

      {/* Games Tab Content */}
      {activeTab === "games" && (
        <div className={styles.gamesGrid}>
          {games.map((game) => (
            <div
              key={game.gameId}
              className={styles.gameTile}
              onClick={() => navigate(`/game/${game.gameId}`)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/game/${game.gameId}`);
                }
              }}
            >
              <img
                src={game.coverUrl || noImage}
                alt={game.name}
                className={styles.gameCover}
                loading="lazy"
                onError={(e) => {
                  if (e.currentTarget.src !== noImage) {
                    e.currentTarget.src = noImage;
                  }
                }}
              />
              <div className={styles.gameTitle}>{game.name}</div>
            </div>
          ))}

          {!gamesLoading && games.length === 0 && (
            <div className={styles.noResults}>No games found.</div>
          )}

          {gamesLoading && (
            <div className={styles.gamesLoading}>
              <div className={styles.spinner} />
            </div>
          )}

          {gamesHasMore && !gamesLoading && (
            <div ref={gamesSentinelRef} className={styles.sentinel} />
          )}
        </div>
      )}
    </div>
  );
};

export default Discover;
